/**
 * Waar de tekstballon staat, naast het aangewezen element.
 *
 * Bij voorkeur rechts ervan: de meeste ankers staan in de rail links. Past dat
 * niet, dan eronder, erboven of links ervan; past niets, dan in het midden. De
 * ballon blijft altijd minstens `MARGIN` van de rand, ook op een telefoon.
 */
export interface Rect {
    top: number;
    left: number;
    width: number;
    height: number;
}

export interface Size {
    width: number;
    height: number;
}

export interface Position {
    top: number;
    left: number;
}

/** De rand rond het scherm: gelijk aan de goot van een telefoon. */
export const MARGIN = 16;

/** De ruimte tussen het aangewezen element en de ballon. */
export const GAP = 12;

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(value, Math.max(min, max)));
}

export function centered(balloon: Size, viewport: Size): Position {
    return {
        top: clamp(
            (viewport.height - balloon.height) / 2,
            MARGIN,
            viewport.height - balloon.height - MARGIN,
        ),
        left: clamp(
            (viewport.width - balloon.width) / 2,
            MARGIN,
            viewport.width - balloon.width - MARGIN,
        ),
    };
}

export function placeBalloon(target: Rect | null, balloon: Size, viewport: Size): Position {
    if (!target) {
        return centered(balloon, viewport);
    }

    const right = target.left + target.width;
    const bottom = target.top + target.height;
    const middleY = clamp(
        target.top + target.height / 2 - balloon.height / 2,
        MARGIN,
        viewport.height - balloon.height - MARGIN,
    );
    const middleX = clamp(
        target.left + target.width / 2 - balloon.width / 2,
        MARGIN,
        viewport.width - balloon.width - MARGIN,
    );

    if (right + GAP + balloon.width <= viewport.width - MARGIN) {
        return { top: middleY, left: right + GAP };
    }

    if (bottom + GAP + balloon.height <= viewport.height - MARGIN) {
        return { top: bottom + GAP, left: middleX };
    }

    if (target.top - GAP - balloon.height >= MARGIN) {
        return { top: target.top - GAP - balloon.height, left: middleX };
    }

    if (target.left - GAP - balloon.width >= MARGIN) {
        return { top: middleY, left: target.left - GAP - balloon.width };
    }

    return centered(balloon, viewport);
}
